import { useEffect, useMemo, useState } from "react";

import {
  faArrowLeft,
  faFilter,
  faTrashCan,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { faFilter as faFilterSolid } from "@fortawesome/pro-solid-svg-icons";
import { SelectRangeEventHandler } from "react-day-picker";
import { FieldError, FormProvider, Resolver, useForm } from "react-hook-form";

import { Button } from "src/base-components/Button";
import {
  DateRange,
  DateRangePicker,
} from "src/base-components/DateRangePicker";
import { Icon } from "src/base-components/Icon";
import { CustomPopover } from "src/base-components/Popover";
import { DEFAULT_FILTER_FORM_VALUES } from "src/eventsCatalogue/filters/constants";
import { EventFilterGroups } from "src/eventsCatalogue/filters/EventFilterGroups";
import { EventFilterFormData } from "src/eventsCatalogue/filters/types";
import {
  getEventProperties,
  getValidationError,
} from "src/eventsCatalogue/filters/utils";
import { UrlFilterParams } from "src/eventsCatalogue/hooks/useEventFiltersUrl";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { findEventSchema } from "src/eventsCatalogue/utils";
import { SubHeaderButton } from "src/layout/SubHeader";

type Props = {
  eventType: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onApplyFilters: (
    formData: EventFilterFormData,
    dateRange: DateRange | undefined,
  ) => void;
  onClearFilters: () => void;
  dateRangeValue: DateRange | undefined;
  onDateRangeChange: SelectRangeEventHandler;
  hasFilterCondition?: boolean;
  /** URL-derived filter params for restoring form state */
  urlFilterParams?: UrlFilterParams | null;
  onClickBack?: () => void;
  buttonRenderer?: (props: ButtonRendererProps) => React.ReactNode;
};

type ButtonRendererProps = {
  isActive: boolean;
  onClick: () => void;
};

const defaultButtonRenderer = ({ isActive, onClick }: ButtonRendererProps) => {
  return (
    <SubHeaderButton
      icon={isActive ? faFilterSolid : faFilter}
      tooltip="All filters"
      tooltipPlacement="right"
      variant={isActive ? "active" : "default"}
      onClick={onClick}
    />
  );
};

// Internal component that manages the form instance
const EventsFilterDropdownForm: React.FC<Props> = ({
  eventType,
  isOpen,
  onOpenChange,
  onApplyFilters,
  onClearFilters,
  dateRangeValue,
  onDateRangeChange,
  hasFilterCondition = false,
  urlFilterParams,
  onClickBack,
  buttonRenderer = defaultButtonRenderer,
}) => {
  const { data: eventSchemas } = useEventSchemas();
  const currentEvent = findEventSchema(eventType, eventSchemas);
  const eventProperties = useMemo(
    () => getEventProperties(currentEvent),
    [currentEvent],
  );

  const [localDateRangeValue, setLocalDateRangeValue] = useState<
    DateRange | undefined
  >(urlFilterParams?.dateRange ?? dateRangeValue);

  const handleDateRangeChange: SelectRangeEventHandler = (
    range: DateRange | undefined,
  ) => {
    setLocalDateRangeValue(range);
  };

  // Helper function to validate condition value
  const validateConditionValue = (
    value: string,
    fieldName: string,
    fieldPath: string,
    errors: Record<string, FieldError>,
  ): void => {
    if (!value.trim()) {
      errors[`${fieldPath}.value`] = {
        type: "required",
        message: "Value is required",
      };
      return;
    }

    // Get field type for type validation
    const fieldProperty = eventProperties.find((p) => p.name === fieldName);

    // Validate value type (number, integer, string)
    const typeValidationError = getValidationError(
      fieldProperty?.datatype,
      value,
    );
    if (typeValidationError) {
      errors[`${fieldPath}.value`] = {
        type: "validate",
        message: typeValidationError,
      };
    }
  };

  // Custom validator for event filter form
  const validator: Resolver<EventFilterFormData> = (formValues) => {
    const errors = {} as Record<string, FieldError>;
    const groups = formValues.filter_condition?.conditions || [];

    // Validate each group
    groups.forEach((group, groupIndex) => {
      const conditions = group?.conditions || [];

      // Each group must have at least one condition
      if (conditions.length === 0) {
        errors[`filter_condition.conditions.${groupIndex}.conditions`] = {
          type: "required",
          message: "At least one condition is required",
        };
        return;
      }

      // Validate each condition
      conditions.forEach((condition, conditionIndex) => {
        const fieldPath = `filter_condition.conditions.${groupIndex}.conditions.${conditionIndex}`;

        // Field is always required
        if (!condition.field?.trim()) {
          errors[`${fieldPath}.field`] = {
            type: "required",
            message: "Field is required",
          };
        }

        // Value is required unless operator is "is_none"
        if (condition.operator !== "is_none") {
          validateConditionValue(
            condition.value,
            condition.field,
            fieldPath,
            errors,
          );
        }
      });
    });

    return {
      values: Object.keys(errors).length > 0 ? {} : formValues,
      errors,
    };
  };

  const form = useForm<EventFilterFormData>({
    resolver: validator,
    defaultValues: urlFilterParams?.formData ?? DEFAULT_FILTER_FORM_VALUES,
    mode: "onChange",
  });

  const formData = form.watch();

  // Reset form when filters are cleared externally (e.g., from error state)
  // or restore form state from URL params when they change
  useEffect(() => {
    if (urlFilterParams) {
      form.reset(urlFilterParams.formData);
      setLocalDateRangeValue(urlFilterParams.dateRange);
    } else if (!hasFilterCondition) {
      form.reset(DEFAULT_FILTER_FORM_VALUES);
      setLocalDateRangeValue(dateRangeValue);
    }
  }, [hasFilterCondition, urlFilterParams, form, dateRangeValue]);

  const handleApplyFilters = () => {
    onOpenChange(false);
    onApplyFilters(formData, localDateRangeValue);
    if (onDateRangeChange) {
      onDateRangeChange(
        localDateRangeValue,
        new Date(),
        {},
        {} as React.MouseEvent,
      );
    }
  };

  const isFilterApplied = isOpen || hasFilterCondition;

  const handleClearFilters = () => {
    form.reset(DEFAULT_FILTER_FORM_VALUES);
    setLocalDateRangeValue(dateRangeValue);
    onClearFilters();
  };

  return (
    <CustomPopover
      button={buttonRenderer({
        isActive: isFilterApplied,
        onClick: () => onOpenChange(!isOpen),
      })}
      className="w-[700px]"
      dataLoc="events-filter-dropdown"
      isOpen={isOpen}
    >
      <div className="divide-y divide-gray-200">
        {/* Header with date range */}
        <div className="flex items-center justify-between gap-x-3 px-4 pb-2">
          <div className="flex items-center gap-x-3">
            {onClickBack && (
              <Icon
                color="text-gray-500 hover:text-gray-700"
                icon={faArrowLeft}
                size="xs"
                onClick={onClickBack}
              />
            )}
            <span className="whitespace-nowrap font-inter-medium-12px text-gray-700">
              Show {currentEvent?.display_name_plural} for
            </span>
            <span className="cursor-pointer">
              <DateRangePicker
                value={localDateRangeValue}
                usePredefinedDates
                onChange={handleDateRangeChange}
              />
            </span>
          </div>
          <CustomPopover.Button
            data-loc="events-filter-close-button"
            onClick={() => onOpenChange(false)}
          >
            <Icon
              color="text-gray-500 hover:text-gray-700"
              icon={faXmark}
              size="xs"
            />
          </CustomPopover.Button>
        </div>

        {/* Filter Conditions */}
        <div className="p-4">
          <FormProvider {...form}>
            <EventFilterGroups eventType={eventType} />
          </FormProvider>
        </div>

        {/* Footer with Clear and Apply buttons */}
        <div className="flex justify-between px-4 pt-4 pb-2">
          <CustomPopover.Button
            as={Button}
            dataLoc="events-filter-clear-button"
            iconLeft={faTrashCan}
            size="sm"
            variant="secondary"
            onClick={handleClearFilters}
          >
            Clear filters
          </CustomPopover.Button>
          <CustomPopover.Button
            as={Button}
            dataLoc="events-filter-apply-button"
            disabled={!form.formState.isValid}
            size="sm"
            variant="primary"
            onClick={handleApplyFilters}
          >
            Apply
          </CustomPopover.Button>
        </div>
      </div>
    </CustomPopover>
  );
};

/**
 * Wrapper component that ensure that the EventsFilterDropdownForm is remounted when eventType changes.
 * The `key` prop is critical here - it ensures the form state is reset when switching
 * between different event types.
 *
 * DO NOT refactor this wrapper away - the key prop pattern is intentional and necessary.
 */
export const EventsFilterDropdown: React.FC<Props> = (props) => {
  return <EventsFilterDropdownForm key={props.eventType} {...props} />;
};
